import { supabase } from '@/lib/supabase';
import { AnalysisMode, AnalysisConfig, AnalysisResult, PredictionResult } from '@/types/trading-analysis-types';
import { AnalyzerFactory } from './AnalyzerFactory';

export class TradingAnalysisOrchestrator {
  /**
   * Analyze patterns with specified mode and configuration
   */
  static async analyzePatterns(
    fileId: string,
    mode: AnalysisMode = 'basic',
    customConfig?: Partial<AnalysisConfig>
  ): Promise<AnalysisResult> {
    try {
      // Get trading sequence from database
      const sequence = await this.getTradingSequence(fileId);
      
      if (sequence.length === 0) {
        throw new Error('No trading data found');
      }

      // Create analyzer for specified mode
      const analyzer = AnalyzerFactory.createAnalyzer(mode, customConfig);
      
      // Perform analysis
      const result = analyzer.analyzePatterns(sequence);
      
      // Store results in database
      await this.storeAnalysisResults(fileId, result, mode, customConfig);
      
      return result;
      
    } catch (error) {
      console.error('Analysis failed:', error);
      throw error;
    }
  }

  /**
   * Make prediction using specified mode
   */
  static async predictNextTrade(
    fileId: string,
    inputSequence: string,
    mode: AnalysisMode = 'basic',
    customConfig?: Partial<AnalysisConfig>
  ): Promise<PredictionResult> {
    const results = await this.batchPredictTrades(fileId, [inputSequence], mode, customConfig);
    return results[0];
  }

  /**
   * PERFORMANCE CRITICAL: True Batch API Processing via Supabase Edge Function
   * Process multiple predictions in one optimized server call
   */
  static async batchPredictTrades(
    fileId: string,
    inputSequences: string[],
    mode: AnalysisMode = 'basic',
    customConfig?: Partial<AnalysisConfig>
  ): Promise<PredictionResult[]> {
    try {
      if (inputSequences.length === 0) return [];

      console.log(`Processing ${inputSequences.length} predictions via Edge Function`);
      const startTime = Date.now();

      // Call Supabase Edge Function for true batch processing
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('User not authenticated');

      const response = await fetch('https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/batch-trading-prediction', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileId,
          inputSequences,
          analysisMode: mode,
          customConfig
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Edge Function failed: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Batch prediction failed');
      }

      const processingTime = Date.now() - startTime;
      console.log(`Batch prediction completed in ${processingTime}ms (${result.processingTime}ms server-side)`);
      console.log(`Used ${result.patternsUsed} patterns for ${result.predictions.length} predictions`);

      return result.predictions.map((pred: any) => ({
        winProbability: pred.winProbability,
        lossProbability: pred.lossProbability,
        confidenceLevel: pred.confidenceLevel,
        matchingPatterns: pred.matchingPatterns,
        exactMatches: pred.exactMatches || 0,
        prediction: pred.prediction || 'UNCERTAIN',
        reason: pred.reason || 'Pattern-based prediction',
        fuzzyMatches: pred.fuzzyMatches,
        statisticalConfidence: pred.statisticalConfidence,
        ensembleScore: pred.ensembleScore,
        anomalyScore: pred.anomalyScore,
        debugInfo: pred.debugInfo // Add missing debugInfo field
      }));
      
    } catch (error) {
      console.error('Batch prediction via Edge Function failed:', error);
      
      // Fallback to client-side processing if Edge Function fails
      console.log('Falling back to client-side processing...');
      return this.batchPredictTradesClientSide(fileId, inputSequences, mode, customConfig);
    }
  }

  /**
   * Fallback client-side batch processing (original implementation)
   */
  private static async batchPredictTradesClientSide(
    fileId: string,
    inputSequences: string[],
    mode: AnalysisMode = 'basic',
    customConfig?: Partial<AnalysisConfig>
  ): Promise<PredictionResult[]> {
    // Get existing patterns from database ONCE
    const patterns = await this.getStoredPatterns(fileId, mode);
    
    if (patterns.length === 0) {
      throw new Error('No patterns available for prediction. Please analyze patterns first.');
    }

    // Create analyzer ONCE for the batch
    const analyzer = AnalyzerFactory.createAnalyzer(mode, customConfig);
    
    // Process all predictions in memory (no DB calls per prediction)
    const batchResults: PredictionResult[] = [];
    
    for (const inputSequence of inputSequences) {
      const prediction = analyzer.predictNextTrade(inputSequence, patterns);
      
      // Convert legacy debugInfo structure to new structure if needed
      let debugInfo = undefined;
      if ('debugInfo' in prediction && prediction.debugInfo) {
        const oldDebugInfo = prediction.debugInfo;
        debugInfo = {
          matchedPatterns: oldDebugInfo.exactMatches?.map(match => ({
            pattern: match.pattern,
            successRate: match.successRate,
            frequency: match.frequency,
            confidenceScore: 0.5, // Default confidence
            contribution: (match.successRate / 100) * 0.5
          })) || [],
          rejectedPatterns: oldDebugInfo.rejectedMatches?.map(match => ({
            pattern: match.pattern,
            reason: match.reason
          })) || [],
          calculationSummary: {
            totalWeight: oldDebugInfo.exactMatches?.length || 0,
            weightedWinRate: oldDebugInfo.datasetStats?.winRate || 50,
            finalAverage: (oldDebugInfo.datasetStats?.winRate || 50) / 100
          },
          // Keep legacy fields for backward compatibility
          datasetStats: oldDebugInfo.datasetStats,
          exactMatches: oldDebugInfo.exactMatches,
          rejectedMatches: oldDebugInfo.rejectedMatches,
          qualityScore: oldDebugInfo.qualityScore,
          biasWarning: oldDebugInfo.biasWarning
        };
      }
      
      batchResults.push({
        ...prediction,
        exactMatches: prediction.exactMatches || 0,
        prediction: prediction.prediction || 'UNCERTAIN',
        reason: prediction.reason || 'Pattern-based prediction',
        debugInfo
      });
    }

    // Batch store all predictions in ONE database call (client-side only)
    if (batchResults.length > 0) {
      await this.batchStorePredictions(fileId, inputSequences, batchResults, mode);
    }
    
    return batchResults;
  }

  /**
   * Compare performance across different modes
   */
  static async compareAnalysisModes(fileId: string): Promise<{
    basic: AnalysisResult;
    intermediate: AnalysisResult;
    advanced: AnalysisResult;
    comparison: {
      processingTime: { basic: number; intermediate: number; advanced: number };
      patternCount: { basic: number; intermediate: number; advanced: number };
      averageConfidence: { basic: number; intermediate: number; advanced: number };
    };
  }> {
    const sequence = await this.getTradingSequence(fileId);
    
    // Run all three modes
    const basicAnalyzer = AnalyzerFactory.createAnalyzer('basic');
    const intermediateAnalyzer = AnalyzerFactory.createAnalyzer('intermediate');
    const advancedAnalyzer = AnalyzerFactory.createAnalyzer('advanced');
    
    const basicResult = basicAnalyzer.analyzePatterns(sequence);
    const intermediateResult = intermediateAnalyzer.analyzePatterns(sequence);
    const advancedResult = advancedAnalyzer.analyzePatterns(sequence);
    
    // Calculate comparison metrics
    const comparison = {
      processingTime: {
        basic: basicResult.processingTime,
        intermediate: intermediateResult.processingTime,
        advanced: advancedResult.processingTime
      },
      patternCount: {
        basic: basicResult.totalPatterns,
        intermediate: intermediateResult.totalPatterns,
        advanced: advancedResult.totalPatterns
      },
      averageConfidence: {
        basic: this.calculateAverageConfidence(basicResult.patterns),
        intermediate: this.calculateAverageConfidence(intermediateResult.patterns),
        advanced: this.calculateAverageConfidence(advancedResult.patterns)
      }
    };
    
    return {
      basic: basicResult,
      intermediate: intermediateResult,
      advanced: advancedResult,
      comparison
    };
  }

  /**
   * Get trading sequence from database
   */
  private static async getTradingSequence(fileId: string): Promise<number[]> {
    const { data, error } = await supabase
      .from('trading_files')
      .select('trading_data')
      .eq('id', fileId)
      .single();

    if (error) throw error;
    
    return data?.trading_data || [];
  }

  /**
   * Store analysis results in database
   */
  private static async storeAnalysisResults(
    fileId: string,
    result: AnalysisResult,
    mode: AnalysisMode,
    config?: Partial<AnalysisConfig>
  ): Promise<void> {
    // Store comprehensive analysis results
    await this.storeComprehensiveResults(fileId, result, mode, config);
    
    // Delete existing patterns for this file and mode
    await supabase
      .from('trading_patterns')
      .delete()
      .eq('file_id', fileId)
      .eq('analysis_mode', mode);

    // Insert new patterns
    const patterns = result.patterns.map(pattern => ({
      file_id: fileId,
      pattern: pattern.pattern,
      pattern_length: pattern.pattern?.length || 0,
      frequency: pattern.frequency,
      next_win_count: pattern.nextWinCount,
      next_loss_count: pattern.nextLossCount,
      success_rate: pattern.successRate,
      confidence_score: pattern.confidenceScore,
      analysis_mode: mode,
      similarity_threshold: config?.similarityThreshold,
      statistical_metrics: pattern.statisticalMetrics,
      recency_weight: pattern.recencyWeight,
      length_weight: pattern.lengthWeight,
      last_seen_index: pattern.lastSeenIndex
    }));

    if (patterns.length > 0) {
      const { error } = await supabase
        .from('trading_patterns')
        .insert(patterns);

      if (error) throw error;
    }
  }

  /**
   * Store comprehensive analysis results
   */
  private static async storeComprehensiveResults(
    fileId: string,
    result: AnalysisResult,
    mode: AnalysisMode,
    config?: Partial<AnalysisConfig>
  ): Promise<void> {
    const analysisData = {
      file_id: fileId,
      analysis_mode: mode,
      total_patterns: result.totalPatterns,
      processing_time: result.processingTime,
      pattern_distribution: result.patternDistribution || null,
      fuzzy_matches: result.fuzzyMatches || null,
      statistically_significant: result.statisticallySignificant || null,
      cross_validation_score: result.crossValidationScore || null,
      ensemble_accuracy: result.ensembleAccuracy || null,
      analysis_config: config || null,
      avg_confidence_score: this.calculateAverageConfidence(result.patterns),
      high_risk_patterns_count: result.highRiskPatterns?.length || 0,
      opportunity_patterns_count: result.opportunityPatterns?.length || 0
    };

    const { error } = await supabase
      .from('trading_analysis_results')
      .upsert(analysisData, { onConflict: 'file_id,analysis_mode' });

    if (error) throw error;
  }

  /**
   * Get stored patterns from database
   */
  private static async getStoredPatterns(fileId: string, mode: AnalysisMode): Promise<any[]> {
    const { data, error } = await supabase
      .from('trading_patterns')
      .select('*')
      .eq('file_id', fileId)
      .eq('analysis_mode', mode)
      .order('confidence_score', { ascending: false });

    if (error) throw error;

    // Import PatternClassifier for dynamic classification
    const { PatternClassifier } = await import('./utils/PatternClassifier');

    return (data || []).map(row => {
      const pattern: any = {
        pattern: row.pattern,
        frequency: row.frequency,
        nextWinCount: row.next_win_count,
        nextLossCount: row.next_loss_count,
        successRate: row.success_rate,
        confidenceScore: row.confidence_score,
        statisticalMetrics: row.statistical_metrics,
        similarities: [], // Would be populated in advanced mode
        recencyWeight: row.recency_weight,
        lengthWeight: row.length_weight,
        lastSeenIndex: row.last_seen_index
      };
      
      // Add classification for filtering
      pattern.classification = PatternClassifier.classifyPattern(pattern);
      
      return pattern;
    });
  }

  /**
   * Store prediction in database (legacy single prediction)
   */
  private static async storePrediction(
    fileId: string,
    inputSequence: string,
    prediction: PredictionResult,
    mode: AnalysisMode
  ): Promise<void> {
    await this.batchStorePredictions(fileId, [inputSequence], [prediction], mode);
  }

  /**
   * PERFORMANCE CRITICAL: Batch store predictions in ONE database call
   */
  private static async batchStorePredictions(
    fileId: string,
    inputSequences: string[],
    predictions: PredictionResult[],
    mode: AnalysisMode
  ): Promise<void> {
    if (predictions.length === 0) return;

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Prepare batch insert data
    const batchData = predictions.map((prediction, index) => ({
      file_id: fileId,
      user_id: user.id,
      input_sequence: inputSequences[index],
      sequence_length: inputSequences[index].length,
      win_probability: prediction.winProbability,
      loss_probability: prediction.lossProbability,
      confidence_level: prediction.confidenceLevel,
      matching_patterns: prediction.matchingPatterns,
      analysis_mode: mode,
      fuzzy_matches: prediction.fuzzyMatches || null,
      statistical_confidence: prediction.statisticalConfidence || null,
      ensemble_score: prediction.ensembleScore || null,
      anomaly_score: prediction.anomalyScore || null,
      prediction_status: prediction.prediction,
      prediction_reason: prediction.reason,
      exact_matches: prediction.exactMatches
    }));

    // Single batch insert instead of multiple individual inserts
    const { error } = await supabase
      .from('pattern_predictions')
      .insert(batchData);

    if (error) throw error;
  }

  /**
   * Calculate average confidence score
   */
  private static calculateAverageConfidence(patterns: any[]): number {
    if (patterns.length === 0) return 0;
    
    const total = patterns.reduce((sum, p) => sum + (p.confidenceScore || 0), 0);
    return Math.round((total / patterns.length) * 100) / 100;
  }

  /**
   * Get comprehensive analysis results
   */
  static async getAnalysisResults(fileId: string, mode: AnalysisMode): Promise<AnalysisResult | null> {
    const { data, error } = await supabase
      .from('trading_analysis_results')
      .select('*')
      .eq('file_id', fileId)
      .eq('analysis_mode', mode)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No data found
      throw error;
    }

    if (!data) return null;

    // Get patterns for this file and mode
    const patterns = await this.getStoredPatterns(fileId, mode);

    // Filter patterns with minimum frequency (consistent with analysis time)
    const filteredPatterns = patterns.filter(p => p.frequency >= 3);

    // Apply same classification filtering as during analysis
    const highRiskPatterns = filteredPatterns.filter(p => 
      p.classification?.category === 'HIGH_LOSS' || p.classification?.category === 'MODERATE_LOSS'
    );
    
    const opportunityPatterns = filteredPatterns.filter(p =>
      p.classification?.category === 'HIGH_WIN' || p.classification?.category === 'MODERATE_WIN'
    );

    // Debug logging to track consistency
    console.log('🔧 Analysis Results Debug:', {
      fileId: fileId.substring(0, 8),
      mode,
      totalPatterns: data.total_patterns,
      cachedHighRisk: data.high_risk_patterns_count,
      cachedOpportunity: data.opportunity_patterns_count,
      calculatedHighRisk: highRiskPatterns.length,
      calculatedOpportunity: opportunityPatterns.length,
      allPatternsCount: patterns.length,
      filteredPatternsCount: filteredPatterns.length
    });

    // Use cached counts if they match calculated counts (consistency check)
    const useConsistentResults = (
      data.high_risk_patterns_count === highRiskPatterns.length &&
      data.opportunity_patterns_count === opportunityPatterns.length
    );

    if (!useConsistentResults) {
      console.warn('⚠️ Inconsistency detected between cached and calculated values:', {
        cached: { highRisk: data.high_risk_patterns_count, opportunity: data.opportunity_patterns_count },
        calculated: { highRisk: highRiskPatterns.length, opportunity: opportunityPatterns.length }
      });
    }

    return {
      mode,
      patterns: filteredPatterns, // Use filtered patterns for consistency
      totalPatterns: data.total_patterns,
      processingTime: data.processing_time,
      patternDistribution: data.pattern_distribution,
      fuzzyMatches: data.fuzzy_matches,
      statisticallySignificant: data.statistically_significant,
      crossValidationScore: data.cross_validation_score,
      ensembleAccuracy: data.ensemble_accuracy,
      highRiskPatterns,
      opportunityPatterns,
      // Add cached counts for UI display
      highRiskPatternsCount: data.high_risk_patterns_count,
      opportunityPatternsCount: data.opportunity_patterns_count
    };
  }

  /**
   * Get available analysis modes for a file
   */
  static async getAvailableAnalysisModes(fileId: string): Promise<AnalysisMode[]> {
    const { data, error } = await supabase
      .from('trading_analysis_results')
      .select('analysis_mode')
      .eq('file_id', fileId);

    if (error) throw error;
    
    return (data || []).map(row => row.analysis_mode as AnalysisMode);
  }

  /**
   * Get analysis history for a file
   */
  static async getAnalysisHistory(fileId: string) {
    const { data, error } = await supabase
      .from('trading_analysis_results')
      .select('*')
      .eq('file_id', fileId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    return (data || []).map(row => ({
      analysis_mode: row.analysis_mode,
      created_at: row.created_at,
      pattern_count: row.total_patterns,
      avg_confidence: row.avg_confidence_score,
      processing_time: row.processing_time,
      high_risk_count: row.high_risk_patterns_count,
      opportunity_count: row.opportunity_patterns_count
    }));
  }

  /**
   * Get prediction history for a file
   */
  static async getPredictionHistory(fileId: string) {
    const { data, error } = await supabase
      .from('pattern_predictions')
      .select('*')
      .eq('file_id', fileId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) throw error;
    return data || [];
  }
}